import { useState, useCallback, useMemo } from 'react';
import { MediaFile, UploadType } from '../types';
import { ACCEPTED_FILE_TYPES, FILE_SIZE_LIMITS } from '../constants';
import { generateFileId, isDocument, validateFileSize } from '../utils';

interface UseFileManagerProps {
  uploadType: UploadType;
  maxFiles: number;
  existingFilesCount: number;
  onUploadError?: (error: string) => void;
}

export const useFileManager = ({
  uploadType,
  maxFiles,
  existingFilesCount,
  onUploadError,
}: UseFileManagerProps) => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

  const acceptedFileTypes = useMemo(() => ACCEPTED_FILE_TYPES[uploadType], [uploadType]);

  const maxFileSize = useMemo(() => {
    return uploadType === 'images' ? FILE_SIZE_LIMITS.VIDEO.BYTES : FILE_SIZE_LIMITS.DOCUMENT.BYTES;
  }, [uploadType]);

  const canAddMoreFiles = useMemo(() => {
    return mediaFiles.length + existingFilesCount < maxFiles;
  }, [mediaFiles.length, existingFilesCount, maxFiles]);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!canAddMoreFiles) {
        const error = `No puedes agregar más de ${maxFiles} archivos en total`;
        onUploadError?.(error);
        return;
      }

      const filesToProcess = acceptedFiles.slice(
        0,
        maxFiles - mediaFiles.length - existingFilesCount
      );

      if (filesToProcess.length < acceptedFiles.length) {
        const error = `Solo se procesaron ${filesToProcess.length} archivos debido al límite de ${maxFiles} archivos`;
        onUploadError?.(error);
      }

      try {
        const processed: MediaFile[] = [];

        for (const file of filesToProcess) {
          validateFileSize(file);
          const preview = isDocument(file.type) ? '' : URL.createObjectURL(file);

          processed.push({
            file: file,
            preview,
            id: generateFileId(),
          });
        }

        setMediaFiles((prev) => [...prev, ...processed]);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al procesar los archivos';
        console.error('File processing error:', err);
        onUploadError?.(errorMessage);
      }
    },
    [canAddMoreFiles, maxFiles, mediaFiles.length, existingFilesCount, onUploadError]
  );

  const removeFile = useCallback((id: string) => {
    setMediaFiles((prev) => {
      const updated = prev.filter((file) => file.id !== id);
      const removedFile = prev.find((file) => file.id === id);

      if (removedFile?.preview) {
        URL.revokeObjectURL(removedFile.preview);
      }

      return updated;
    });
  }, []);

  const clearAllFiles = useCallback(() => {
    mediaFiles.forEach((file) => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    setMediaFiles([]);
  }, [mediaFiles]);

  return {
    mediaFiles,
    acceptedFileTypes,
    maxFileSize,
    canAddMoreFiles,
    handleDrop,
    removeFile,
    clearAllFiles,
  };
};
